<!-- eslint-disable vue/no-restricted-html-elements -->
<!-- eslint-disable max-len -->
<template>
  <div class="site-header-container">
    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="void"
      namespace="menuDrawer"
      :template="false"
      type="modal"
    >
      <template #body="{ isActive }">
        <menu-drawer
          :handle="mobile.handle"
          :is-active="isActive"
          :item-count="mobile.items.length"
          :items="mobile.items"
          :mega-menu-theme="megaMenuTheme"
          :slides="mobileSlides"
        />
      </template>
    </overlay>

    <!-- Search drawer -->
    <overlay
      key="searchDrawer"
      block-class="search-drawer-overlay"
      direction="left"
      namespace="searchDrawer"
      type="drawer"
    >
      <template #body="{ isActive }">
        <search-drawer
          v-cloak
          :is-active="isActive"
          :min-chars="search.minChars"
          :merchant="search.merchant"
          :popular-searches="search.popularSearches"
          :whats-new="search.whatsNew"
          :product-limit="search.productLimit"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} displayNavElements - Shows/hides elements in the nav.
 * @param {Object} headerTheme - Background and content themes for the header.
 * @param {Object} megaMenuTheme - Theme settings for the mega menu.
 * @param {Object} megaNavCta - Mega Nav CTA settings.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Array} [mobileSlides] - Array of mobile menu slides.
 * @param {Array} [preHeaderBannerContent] - Array of banner slides.
 * @param {Boolean} [preHeaderBannerDismissable] - Sets whether to make banner
 * able to be dismissed.
 * @param {String} preHeaderBannerId - ID for the pre-header banner.
 * @param {Number} [preHeaderBannerInterval] - Pre-header interval in ms.
 * @param {Boolean} [showPreHeaderBanner] - Toggles whether to display the
 * pre-header banner or not.
 * @param {Object} [preHeaderBannerTheme] - Defines background and content for
 * pre-header banner component.
 * @param {Object} [preHeaderTransitionSpeed] - Defines transition speed on pre
 * header banner in seconds.
 * @param {Array} [slides] - Array of slide objects created from section blocks.
 *
 */
import { isVisible } from '~/helpers/general'

import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import MenuDrawer from '~async/menu-drawer/menu-drawer'
import Overlay from '~global/overlay/overlay'
import SearchDrawer from '~async/search-drawer/search-drawer'

export default {
  name: 'SiteHeader',

  components: {
    MenuDrawer,
    Overlay,
    SearchDrawer,
  },

  props: {
    headerTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    megaMenuTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },

    mobileSlides: {
      type: Array,
      default: () => [],
    },

    search: {
      type: Object,
      default: () => ({
        apiKey: '',
        filters: '',
        minChars: '',
        popularSearches: '',
        whatsNew: '',
        productLimit: '',
        subdomain: '',
      }),
    },

    showPreHeaderBanner: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      displayMegaMenu: false,
      height: {
        total: 0,
        visible: 0,
        announcement: 0,
      },
      theme: {
        desktop: {
          background: null,
          content: null,
        },
        mobile: {
          background: null,
          content: null,
        },
      },
      menuTheme: false,
      mediaObject: {
        image: null,
        link: null,
        text: '',
        error: '',
        textColour: '',
        imagePosition: '',
      },
      mediaObjectFallback: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      bannerDismissed: (state) => state.header.preHeaderDismissed,
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
      scroll: (state) => state.index.scroll.y,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),
  },

  watch: {

    /**
     * Set the global height variables when the breakpoint changes.
     * - needed for ensuring menu drawer is positioned at the correct location.
     */
    breakpoint() {
      this.setHeights()
    },

    /**
     * Set the global height variables when the pre-header banner is dismissed.
     * - needed for ensuring menu drawer is positioned at the correct location.
     */
    bannerDismissed() {
      this.renderBannerStyles()
      setTimeout(() => {
        this.setHeights(true)
      }, this.$timing('normal'))
    },

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      const device = this.isMobile ? 'mobile' : 'desktop'

      if (this.headerTheme[device].background === 'transparent') {
        document.documentElement.style.setProperty(
          '--header-static-height',
          '0px',
        )
      } else {
        document.documentElement.style.setProperty(
          '--header-static-height',
          `${this.height.total}px`,
        )
      }

      this.setHeaderHeight(this.height.total)
    },

    /**
     * Watch announcement height to update CSS variable.
     */
    'height.announcement'() {
      document.documentElement.style.setProperty(
        '--announcement-bar-height',
        `${this.height.announcement}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },

    /**
     * On scroll, we want to check for overlapping sections and update the
     * header theme accordingly.
     * @returns {String}
     */
    scroll: {
      handler() {
        this.updateHeaderTheme()
      },
    },

    /**
     * Watch active overlay to add mobile nav styles to header.
     */
    getActiveOverlay() {
      this.menuTheme = this.getActiveOverlay === 'menuDrawer'

      if (!this.menuTheme) {
        this.updateHeaderTheme()
      }

      /**
       * Set header themes from the overlapped element.
       */
      document.documentElement.style.setProperty(
        '--header-bg-mobile',
        this.megaMenuTheme.mobile.background,
      )
    },
  },

  mounted() {
    nextTick().then(() => {
      this.setHeights(true)
      this.renderBannerStyles()
      this.updateHeaderTheme()
      this.handleMediaBlocks()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
      setHeaderHeight: 'header/setHeaderHeight',
      setVisibleHeight: 'header/setHeaderVisibleHeight',
    }),

    handleMediaBlocks() {
      const primaryMenu = this.$refs.mediaBlock?.find(item => item.dataset.mediaBlockHandle === 'new-1')

      if (primaryMenu) {
        this.mediaObject.image = primaryMenu.querySelector('[data-block-image]')
        this.mediaObject.link = primaryMenu.querySelector('[data-block-link]')
        this.mediaObject.text = primaryMenu.querySelector('[data-block-link-text]')
        this.captureMediaBlockFallback()
      }
    },

    captureMediaBlockFallback() {
      this.mediaObjectFallback = {
        image: this.mediaObject.image.src,
        url: this.mediaObject.link.href,
        text: this.mediaObject.text.innerText,
        textColour: 'black',
        imagePosition: 'top',
      }

      if (this.mediaObject.link.classList.contains('site-header-subnav-media-block__link--white')) {
        this.mediaObjectFallback.textColour = 'white'
      }
    },

    privateSale(title) {
      if (!title) {
        return false
      }

      if (title.includes('--private')) {

        if (typeof title === 'undefined') {
          return false
        }

        if (this.$variable('customer.loggedIn') === true) {
          const customerTier = this.$variable('loyalty.tier')
          if (title.includes(customerTier)) {
            return true
          }
        }
      }
      return false
    },

    handleGtm(event) {
      const dataLayer = window.dataLayer || []

      const target = event.currentTarget

      const navLevel = target.dataset.gtmNavLevel
      const navItem = target.closest('[data-gtm-nav-item]')
      const navCategory = navItem?.querySelector('[data-gtm-nav-level="primary"]')?.innerText

      const navSubBlock = target.closest('[data-gtm-subnav-block]')
      const navSubCategory = navLevel === 'primary' ? 'n/a' : navSubBlock?.querySelector('[data-gtm-subnav-block-header]')?.innerText

      dataLayer.push({
        event: 'navigation_click_desktop',
        navigationClickDesktop: {
          level: navLevel,
          category: navCategory,
          sub_category: navSubCategory,
          click_url: target.href,
          click_text: target.innerText,
        },
      })
    },

    /**
     * Applies styles to header to accommodate collapse of pre-header banner in
     * applicable scenarios.
     */
    renderBannerStyles() {
      if (!this.showPreHeaderBanner) {
        return
      }

      if (!this.stickyHeader) {
        return
      }

      const headerSection = document.querySelector(
        '#shopify-section-site-header',
      )
      const headerElement = document.querySelector('.site-header')
      const preHeader = document.querySelector('.pre-header-banner')
      if (!headerSection || !headerElement || !preHeader) {
        return
      }

      if (this.showPreHeaderBanner && !this.bannerDismissed) {
        const offset = preHeader?.offsetHeight

        /**
         * Set height for the pre-header banner to css variable.
         */
        document.documentElement.style.setProperty(
          '--pre-header-offset',
          `${offset}px`,
        )

        headerSection.classList.add('site-header-section--sticky')
        return
      }

      /**
       * Set height for the pre-header banner to css variable.
       */
      document.documentElement.style.setProperty('--pre-header-offset', '0')
      headerSection.classList.remove('site-header-section--sticky')
    },

    /**
     * Handles the updating of the header theme.
     */
    updateHeaderTheme() {
      const headerElement = document.querySelector('.site-header')

      if (!headerElement) {
        return
      }

      if (!isVisible(headerElement)) {
        return
      }

      const header = headerElement.getBoundingClientRect()
      const sections = [...document.querySelectorAll('[js-header-scroll-activated="true"]')]
      const overlapping = sections.find((sectionData) => {
        const section = sectionData.closest('section')
        if (this.checkElementsOverlap(header, section)) {
          return section
        }
        return false
      })

      const {
        mobileBackground,
        desktopBackground,
        mobileContent,
        desktopContent,
        transparentHeader,
      } = overlapping?.dataset || {
        mobileBackground: null,
        desktopBackground: null,
        mobileContent: null,
        desktopContent: null,
        transparentHeader: null,
      }

      if (transparentHeader === 'true') {
        headerElement.style.setProperty('--header-bg-mobile', 'transparent')
        headerElement.style.setProperty('--header-bg-desktop', 'transparent')
      } else {
        headerElement.style.setProperty('--header-bg-mobile', mobileBackground)
        headerElement.style.setProperty('--header-bg-desktop', desktopBackground)
      }

      headerElement.style.setProperty(
        '--header-color-mobile',
        mobileContent === 'dark' ? 'var(--color-toteme-white)' : 'var(--color-text-primary)',
      )

      headerElement.style.setProperty(
        '--header-color-desktop',
        desktopContent === 'dark' ? 'var(--color-toteme-white)' : 'var(--color-text-primary)',
      )

      this.theme.mobile.content = mobileContent
      this.theme.desktop.content = desktopContent

    },

    /**
     * Checks if the supplied elements are overlapping vertically.
     * @param {Object} domRect1 - Element one bounding rect.
     * @param {Object} element2 - HTMLElement to compare.
     * @returns {Boolean}
     */
    checkElementsOverlap(domRect1, element2) {
      const domRect2 = element2.getBoundingClientRect()
      return (
        domRect1.bottom > domRect2.top && domRect1.bottom < domRect2.bottom
      )
    },

    /**
     * Set heights of fixed header.
     * - height.total is used for body padding.
     * - height.visible is used for overlays etc.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted = false) {
      if (mounted) {
        const siteHeader = document.querySelector('.site-header')
        this.height.total = siteHeader?.offsetHeight
      }

      if (this.showPreHeaderBanner && this.bannerDismissed === this.preHeaderBannerId) {
        this.height.announcement = 0
      }

      const siteHeader = document.querySelector('.site-header')

      /**
       * Visible height is element height combined with translateY value.
       */
      if (siteHeader.classList.contains('is-collapsed')) {
        this.height.visible = 0
      } else {
        this.height.visible = siteHeader?.offsetHeight
      }
    },
  },
}
</script>
